import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, filter, first, switchMap } from 'rxjs/operators';

import { transformNurseryDataLatLong } from '../helpers/dataTransform.helper';
import { Nursery } from '../models/Nursery';
import { Location } from '../models/Location';
import { LocationService } from './location.service';
import { StatesService } from './states.service';

const urlEncodedHeaders = new HttpHeaders({
  'Content-Type': 'application/x-www-form-urlencoded'
});

function toFormData(obj: any): FormData {
  const form = new FormData();
  for (let key in obj) {
    form.append(key, obj[key]);
  }
  return form;
}

@Injectable()
export class NurseryService {
  constructor(
    private http: HttpClient,
    private locationService: LocationService,
    private statesService: StatesService
  ) {}

  getNurseryData(filters: {
    keyword?: string;
    state?: string;
    city?: string;
    zip?: string;
    packagingType?: string;
  }): Observable<Nursery[]> {
    return this.locationService.location.pipe(
      filter(loc => loc !== null),
      first(),
      switchMap(currentLocation => {
        let params: any = {};

        if (filters) {
          if (filters.keyword) params.keyword = filters.keyword;
          if (filters.state) params.state = filters.state;
          if (filters.city) params.city = filters.city;
          if (filters.zip) params.zip = filters.zip;
          if (filters.packagingType) params.packaging_type = filters.packagingType;
        }

        // if there are no filters, force a dummy param
        if (Object.keys(params).length === 0) {
          params._all = 'true';
        }

        return this.http.get<Nursery[]>('nursery', { params }).pipe(
          map(data =>
            data.map(nursery => this.transformNursery(nursery, currentLocation))
          ),
          catchError(this.handleError)
        );
      })
    );
  }

  getNurseryDataId(id: number): Observable<any> {
    return this.locationService.location.pipe(
      filter(loc => loc !== null),
      first(),
      switchMap(currentLocation =>
        this.http.get<any[]>(`nursery/${id}`).pipe(
          map(data => this.transformNursery(data, currentLocation)),
          catchError(this.handleError)
        )
      )
    );
  }

  getNurseryRatingData(): Observable<any> {
    return this.http.get('nursery_rating').pipe(
      map(this.handleNurseryDataRating),
      catchError(this.handleError)
    );
  }

  getNurseryRatingDataId(id: number): Observable<any> {
    return this.http.get(`nursery_rating/${id}`).pipe(
      map(this.handleNurseryDataRating),
      catchError(this.handleError)
    );
  }

  getNurseryProductDataId(id: number): Observable<any> {
    return this.http.get(`product/nursery/${id}`).pipe(
      map(this.handleNurseryProduct),
      catchError(this.handleError)
    );
  }

  getNurseryProductData(): Observable<any> {
    return this.http.get('nursery_product').pipe(
      map(this.handleNurseryProduct),
      catchError(this.handleError)
    );
  }

  getNurseryProductDetailData(): Observable<any> {
    return this.http.get('nursery_product_detail').pipe(
      map(this.handleNurseryProductDetails),
      catchError(this.handleError)
    );
  }

  postNewNursery(nurseryObj: any): Observable<any> {
    return this.http.post('nursery', nurseryObj, { headers: urlEncodedHeaders }).pipe(
      map(this.handlePostNursery),
      catchError(this.handleError)
    );
  }

  updateNursery(nurseryObj: any): Observable<any> {
    const latLng = nurseryObj.lat_long;
    nurseryObj.lat_long = latLng && latLng.lat && latLng.lng ? `${latLng.lat},${latLng.lng}` : '';

    const formData = toFormData({ ...nurseryObj, _method: 'PUT' });

    return this.http.post(`nursery/${nurseryObj.id || -1}`, formData).pipe(
      map(this.handleUpdateNursery),
      catchError(this.handleError)
    );
  }

  removeNursery(nurseryObj: any): Observable<any> {
    const formData = new FormData();
    formData.append('_method', 'DELETE');

    return this.http.post(`nursery/${nurseryObj.id || -1}`, formData).pipe(
      map(this.handleNurseryDeleted),
      catchError(this.handleError)
    );
  }

  uploadNurseryProduct(excelFile: File, nurseryObj: any, userObj: any): Observable<any> {
    const formData = new FormData();
    formData.append('excel', excelFile, excelFile.name);
    formData.append('nursery_id', nurseryObj.id);
    formData.append('user_id', userObj.id);

    return this.http.post('nursery-product-import', formData).pipe(
      map(this.handleNurseryProductUploadSuccess),
      catchError(this.handleError)
    );
  }

  getNurseryPhotos(): Observable<any> {
    return this.http.get('nursery_photo').pipe(
      map(this.handleNurseryPhotos),
      catchError(this.handleError)
    );
  }

  getNurseryPhotosId(id: number): Observable<any> {
    return this.http.get(`nursery_photos/${id}`).pipe(
      map(this.handleNurseryPhotosId),
      catchError(this.handleError)
    );
  }

  uploadNurseryPhoto(photoFile: File, nurseryObj: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', photoFile, photoFile.name);
    formData.append('nursery_id', nurseryObj.id);

    return this.http.post('nursery_photo', formData).pipe(
      map(this.handleNurseryPhotoUploadSuccess),
      catchError(this.handleError)
    );
  }

  updateNurseryPhoto(photoObj: any): Observable<any> {
    const formData = toFormData({ ...photoObj, _method: 'PUT' });

    return this.http.post(`nursery_photo/${photoObj.id}`, formData).pipe(
      map(this.handleNurseryPhotoUpdate),
      catchError(this.handleError)
    );
  }

  removeNurseryPhoto(photoObj: any): Observable<any> {
    const formData = new FormData();
    formData.append('_method', 'DELETE');

    return this.http.post(`nursery_photo/${photoObj.id}`, formData).pipe(
      map(this.handleNurseryPhotoDelete),
      catchError(this.handleError)
    );
  }

  uploadNurseryProductPhoto() {
    console.log('uploadNurseryProductPhoto');
  }

  updateNurseryProductPhoto() {
    console.log('updateNurseryProductPhoto');
  }

  removeNurseryProductPhoto() {
    console.log('removeNurseryProductPhoto');
  }

  // 🔁 Internal Handlers

  private transformNursery(nursery: any, currentLocation: Location): Nursery {
    if (!nursery.lat_long) {
      console.warn(`nursery ${nursery.id} has no lat long`);
    }
    const [lat, lng] = nursery.lat_long.split(',');
    return new Nursery({
      ...nursery,
      lat_lng: {
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      },
      state: this.statesService.getStateFromIdOrDefault(nursery.state_id),
      currentLocation
    });
  }

  private handleNurseryData(res: any) {
    return transformNurseryDataLatLong(res);
  }

  private handleNurseryDataRating = (res: any) => res || {};
  private handleNurseryProduct = (res: any) => res || {};
  private handleNurseryProductDetails = (res: any) => res || {};
  private handleNurseryProductUploadSuccess = (res: any) => res || {};
  private handlePostNursery = (res: any) => res || {};
  private handleUpdateNursery = (res: any) => transformNurseryDataLatLong(res) || {};
  private handleNurseryDeleted = (res: any) => res || {};
  private handleNurseryPhotos = (res: any) => res || {};
  private handleNurseryPhotosId = (res: any) => res || {};
  private handleNurseryPhotoUploadSuccess = (res: any) => res || {};
  private handleNurseryPhotoUpdate = (res: any) => res || {};
  private handleNurseryPhotoDelete = (res: any) => res || {};

  private handleError = (error: any) => {
    console.error('[NurseryService Error]', error);
    return throwError(() => error);
  };
}
